import { Component, ElementRef, Input, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DestinationVideoService } from 'src/app/core/services/destination-video.service';

@Component({
  selector: 'app-assist-left',
  templateUrl: './assist-left.component.html',
  styleUrls: ['./assist-left.component.scss'],
})
export class AssistLeftComponent implements OnInit, OnDestroy {
  @ViewChild('travelVideo') travelVideo!: ElementRef<HTMLVideoElement>;
  @Input() isFloating?: boolean;

  destination: string = 'Global';
  destinationName: string = '';
  isVideoLoaded: boolean = false;
  isMuted: boolean = true;

  benefits = [
    { icon: 'assets/images/assist-icon-02.png', alt: 'No Hidden', text: 'No hidden charges' },
    { icon: 'assets/images/assist-icon-04.png', alt: 'Support', text: 'Lightning fast support' },
    { icon: 'assets/images/assist-icon-03.png', alt: 'AutoPay', text: 'Ease of payment with AutoPay' },
    { icon: 'assets/images/assist-icon-01.png', alt: 'Budget', text: 'Budget friendly travel credit line' }
  ];
  visibleBenefitsCount: number = 0;
  private staggerTimer: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private destinationVideoService: DestinationVideoService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    if (this.isFloating === undefined) {
      const url = this.router.url.toLowerCase();
      this.isFloating = url.includes('sign-up') || url.includes('signup') || url === '/' || url === '/assist';
    }

    const param = this.getRawParam();
    if (param) {
      this.updateDestinationName(param);
    }

    this.route.queryParams.subscribe(params => {
      const p = params['d'] || params['dest'] || params['destination'] || this.getRawParam();
      if (p) {
        this.updateDestinationName(p);
      }
    });

    if (this.isFloating) {
      this.initStaggeredBenefits();
    }
  }

  private initStaggeredBenefits(): void {
    if (typeof window === 'undefined') {
      this.visibleBenefitsCount = this.benefits.length;
      return;
    }

    let started = false;
    const startStagger = () => {
      if (started) return;
      started = true;
      // Brief delay after full page load before the first benefit appears
      this.staggerTimer = setTimeout(() => {
        this.revealNextBenefit();
      }, 500);
    };

    if (document.readyState === 'complete') {
      startStagger();
    } else {
      window.addEventListener('load', () => startStagger(), { once: true });
      // Fallback in case load event already fired before listener or is delayed by streaming
      setTimeout(() => {
        startStagger();
      }, 2500);
    }
  }

  private revealNextBenefit(): void {
    this.ngZone.run(() => {
      if (this.visibleBenefitsCount < this.benefits.length) {
        this.visibleBenefitsCount++;
        this.cdr.detectChanges();

        if (this.visibleBenefitsCount < this.benefits.length) {
          this.staggerTimer = setTimeout(() => {
            this.revealNextBenefit();
          }, 1500); // 1.5 second delay between benefits appearing from top to bottom
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.staggerTimer) {
      clearTimeout(this.staggerTimer);
      this.staggerTimer = null;
    }
  }

  private getRawParam(): string | null {
    if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const p = urlParams.get('d') || urlParams.get('dest') || urlParams.get('destination');
        if (p) return p;
      } catch (e) { }
    }
    try {
      const urlTree = this.router.parseUrl(this.router.url);
      const p = urlTree.queryParams['d'] || urlTree.queryParams['dest'] || urlTree.queryParams['destination'];
      if (p) return p;
    } catch (e) { }
    return this.route.snapshot.queryParamMap.get('d')
      || this.route.snapshot.queryParamMap.get('dest')
      || this.route.snapshot.queryParamMap.get('destination')
      || this.destinationVideoService.getSelectedDestination();
  }

  private updateDestinationName(param: string): void {
    const dest = this.destinationVideoService.resolveDestination(param);
    if (dest && dest.id !== 'global') {
      this.destination = dest.id;
      const nameMap: Record<string, string> = {
        'mumbai': 'Cordelia Cruises',
        'dubai': 'UAE',
        'south-india': 'Kerala',
        'north-india': 'Ladakh and Kashmir',
        'andaman': 'Andaman Islands',
        'europe-1': 'Western Europe',
        'europe-2': 'Southern Europe'
      };
      this.destinationName = nameMap[dest.id] || dest.name;
    }
  }

  onVideoPlay(): void {
    this.isVideoLoaded = true;
  }

  toggleMute(event: Event): void {
    event.stopPropagation();
    if (this.travelVideo && this.travelVideo.nativeElement) {
      this.travelVideo.nativeElement.muted = !this.travelVideo.nativeElement.muted;
      this.isMuted = this.travelVideo.nativeElement.muted;
    }
  }
}

